import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for generating media (images and videos) using the backend API.
 * 
 * This hook encapsulates all logic for:
 * - Making generation requests to the backend
 * - Polling for completion status
 * - Managing loading states and errors
 * - Providing reusable interface across components
 * 
 * @returns {Object} Hook interface with methods and state
 */
export const useMediaGeneration = () => {
    const [loadingStates, setLoadingStates] = useState({});
    const [errors, setErrors] = useState({});
    const [completedMedia, setCompletedMedia] = useState({});
    const pollingIntervals = useRef({});

    /**
     * Generate media (image or video) using the backend API
     * 
     * @param {Object} options - Generation options
     * @param {Object} options.designJson - Polotno design object
     * @param {string} options.mediaType - 'image' or 'video'
     * @param {string} options.outputFormat - 'png', 'jpg', 'gif', or 'mp4'
     * @param {number} [options.width] - Width for videos
     * @param {number} [options.height] - Height for videos
     * @param {number} [options.duration] - Duration for videos in seconds
     * @param {number} [options.fps=30] - FPS for videos
     * @param {number} [options.parentTemplate] - Optional template ID
     * @param {string} [options.requestId] - Unique request ID for tracking
     * 
     * @returns {Promise<Object>} Promise resolving to media data
     */
    const generateMedia = useCallback(async (options) => {
        const {
            designJson,
            mediaType,
            outputFormat,
            width,
            height,
            duration,
            fps = 30,
            parentTemplate,
            requestId = `${mediaType}_${Date.now()}`
        } = options;

        // Validate required parameters
        if (!designJson || !mediaType || !outputFormat) {
            throw new Error('Missing required parameters: designJson, mediaType, outputFormat');
        }

        if (mediaType === 'video' && (!width || !height || !duration)) {
            throw new Error('Video generation requires width, height, and duration');
        }

        try {
            // Set loading state
            setLoadingStates(prev => ({
                ...prev,
                [requestId]: true
            }));
            
            // Clear previous errors
            setErrors(prev => ({
                ...prev,
                [requestId]: null
            }));

            // Prepare request payload
            const payload = {
                design_json: designJson,
                media_type: mediaType,
                output_format: outputFormat,
                ...(mediaType === 'video' && {
                    width,
                    height,
                    duration,
                    fps
                }),
                ...(parentTemplate && { parent_template: parentTemplate })
            };

            // Make API request
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}templates/generate-media/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const mediaData = await response.json();
            
            // Start polling for completion if not completed immediately
            if (mediaData.status === 'pending' || mediaData.status === 'processing') {
                startPolling(mediaData.id, requestId);
            } else if (mediaData.status === 'completed') {
                setCompletedMedia(prev => ({
                    ...prev,
                    [requestId]: mediaData
                }));
                setLoadingStates(prev => ({
                    ...prev,
                    [requestId]: false
                }));
            }

            return {
                success: true,
                data: mediaData,
                requestId
            };

        } catch (error) {
            console.error('Media generation failed:', error);
            
            setErrors(prev => ({
                ...prev,
                [requestId]: error.message || 'Failed to generate media'
            }));
            
            setLoadingStates(prev => ({
                ...prev,
                [requestId]: false
            }));

            throw error;
        }
    }, []);

    /**
     * Start polling for media generation status
     * 
     * @param {number} mediaId - Backend media ID
     * @param {string} requestId - Frontend request ID
     */
    const startPolling = useCallback((mediaId, requestId) => {
        // Clear any existing interval
        if (pollingIntervals.current[requestId]) {
            clearInterval(pollingIntervals.current[requestId]);
        }

        let attempts = 0;
        const maxAttempts = 60; // 5 minutes max (60 * 5 seconds)

        const pollStatus = async () => {
            try {
                attempts++;

                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}templates/media-status/${mediaId}/`,
                    {
                        headers: {
                            'Authorization': `Token ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error(`Status check failed: ${response.status}`);
                }

                const statusData = await response.json();

                if (statusData.status === 'completed') {
                    // Success - stop polling
                    clearInterval(pollingIntervals.current[requestId]);
                    delete pollingIntervals.current[requestId];

                    setCompletedMedia(prev => ({
                        ...prev,
                        [requestId]: statusData
                    }));
                    
                    setLoadingStates(prev => ({
                        ...prev,
                        [requestId]: false
                    }));

                } else if (statusData.status === 'failed') {
                    // Error - stop polling
                    clearInterval(pollingIntervals.current[requestId]);
                    delete pollingIntervals.current[requestId];

                    setErrors(prev => ({
                        ...prev,
                        [requestId]: statusData.error_message || 'Media generation failed'
                    }));
                    
                    setLoadingStates(prev => ({
                        ...prev,
                        [requestId]: false
                    }));

                } else if (attempts >= maxAttempts) {
                    // Timeout - stop polling
                    clearInterval(pollingIntervals.current[requestId]);
                    delete pollingIntervals.current[requestId];

                    setErrors(prev => ({
                        ...prev,
                        [requestId]: 'Media generation timed out'
                    }));
                    
                    setLoadingStates(prev => ({
                        ...prev,
                        [requestId]: false
                    }));
                }
                // Continue polling if status is 'pending' or 'processing'

            } catch (error) {
                console.error('Status polling error:', error);
                
                if (attempts >= maxAttempts) {
                    // Give up after max attempts
                    clearInterval(pollingIntervals.current[requestId]);
                    delete pollingIntervals.current[requestId];

                    setErrors(prev => ({
                        ...prev,
                        [requestId]: 'Failed to check generation status'
                    }));
                    
                    setLoadingStates(prev => ({
                        ...prev,
                        [requestId]: false
                    }));
                }
            }
        };

        // Start polling every 5 seconds
        pollingIntervals.current[requestId] = setInterval(pollStatus, 5000);

    }, []);

    /**
     * Generate image using simplified interface
     */
    const   generateImage = useCallback((designJson, outputFormat = 'png', parentTemplate = null) => {
        return generateMedia({
            designJson,
            mediaType: 'image',
            outputFormat,
            parentTemplate,
            requestId: `image_${Date.now()}`
        });
    }, [generateMedia]);

    /**
     * Generate video using simplified interface
     */
    const generateVideo = useCallback((designJson, { width, height, duration, fps = 30, outputFormat = 'mp4', parentTemplate = null }) => {
        return generateMedia({
            designJson,
            mediaType: 'video',
            outputFormat,
            width,
            height,
            duration,
            fps,
            parentTemplate,
            requestId: `video_${Date.now()}`
        });
    }, [generateMedia]);

    /**
     * Clear error for a specific request
     */
    const clearError = useCallback((requestId) => {
        setErrors(prev => ({
            ...prev,
            [requestId]: null
        }));
    }, []);

    /**
     * Cancel generation (stop polling)
     */
    const cancelGeneration = useCallback((requestId) => {
        if (pollingIntervals.current[requestId]) {
            clearInterval(pollingIntervals.current[requestId]);
            delete pollingIntervals.current[requestId];
        }

        setLoadingStates(prev => ({
            ...prev,
            [requestId]: false
        }));
    }, []);

    /**
     * Cleanup function to clear all intervals on unmount
     */
    useEffect(() => {
        return () => {
            Object.values(pollingIntervals.current).forEach(clearInterval);
        };
    }, []);

    return {
        // Main generation methods
        generateMedia,
        generateImage,
        generateVideo,
        
        // State management
        loadingStates,
        errors,
        completedMedia,
        
        // Utility methods
        clearError,
        cancelGeneration,
        
        // Helper methods
        isLoading: (requestId) => Boolean(loadingStates[requestId]),
        getError: (requestId) => errors[requestId],
        getCompletedMedia: (requestId) => completedMedia[requestId],
        hasCompleted: (requestId) => Boolean(completedMedia[requestId])
    };
};

export default useMediaGeneration; 